import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../state/ApiContext';
import { useUserContext } from '../state/UserContext';
import { installSessionActivityHeartbeat } from '../session-activity';
import { onSessionTerminated, sessionFetch } from '../session-client';

interface SessionCheckContextType {
  isCheckingSession: boolean;
  lastCheckTime: Date | null;
}

const SessionCheckContext = createContext<SessionCheckContextType>({
  isCheckingSession: false,
  lastCheckTime: null,
});

export const SessionCheckProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isCheckingSession, setIsCheckingSession] = useState(false);
  const [lastCheckTime, setLastCheckTime] = useState<Date | null>(null);
  const { userApi } = useApi();
  const { isLoggedIn, setLoggedIn, setUser, getAuthGeneration } =
    useUserContext();
  const navigate = useNavigate();
  const location = useLocation();
  const sessionCheckGeneration = useRef(0);
  const currentLoginState = useRef(isLoggedIn);
  currentLoginState.current = isLoggedIn;

  // Set up an abortable, generation-fenced session check every minute.
  useEffect(() => {
    const generation = ++sessionCheckGeneration.current;
    const authGeneration = getAuthGeneration();
    const controllers = new Set<AbortController>();
    const isCurrent = () =>
      sessionCheckGeneration.current === generation &&
      getAuthGeneration() === authGeneration &&
      currentLoginState.current;
    const redirectToLogin = () => {
      if (!isCurrent()) return;
      const currentPath = location.pathname + location.search + location.hash;

      // Clear user data
      setUser(null);
      setLoggedIn(false);

      // Redirect to login page with redirectUrl
      navigate(`/login?redirectUrl=${encodeURIComponent(currentPath)}`, {
        replace: true,
      });
    };

    const checkSession = async () => {
      if (!isCurrent() || !location.pathname.startsWith('/console')) return;
      const controller = new AbortController();
      controllers.add(controller);
      setIsCheckingSession(true);
      try {
        // The backend uses this flag to avoid extending the session.
        const response = await userApi.apiUserMeGet({
          params: {
            sessionCheck: 'true',
          },
          signal: controller.signal,
        });

        if (!isCurrent()) return;
        if (response.status === 200 && response.data) {
          setLastCheckTime(new Date());
        } else if (response.status === 204) {
          redirectToLogin();
        }
      } catch (error: any) {
        if (
          !isCurrent() ||
          controller.signal.aborted ||
          error?.name === 'CanceledError' ||
          error?.code === 'ERR_CANCELED'
        ) {
          return;
        }
        console.error('Session check failed:', error);

        // A terminal access/refresh failure is an authentication failure.
        if (error.response && [401, 403, 204].includes(error.response.status)) {
          redirectToLogin();
        }
      } finally {
        controllers.delete(controller);
        if (isCurrent()) setIsCheckingSession(false);
      }
    };

    if (isLoggedIn && location.pathname.startsWith('/console')) {
      void checkSession();
    }

    const intervalId = setInterval(() => void checkSession(), 60_000);

    return () => {
      clearInterval(intervalId);
      if (sessionCheckGeneration.current === generation) {
        sessionCheckGeneration.current += 1;
      }
      controllers.forEach((controller) => controller.abort());
      controllers.clear();
    };
  }, [
    isLoggedIn,
    getAuthGeneration,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    setLoggedIn,
    setUser,
    userApi,
  ]);

  // The poll above only notices a dead session on its next minute boundary,
  // and the response that killed it is frequently an unremarkable 204. When
  // the server tells us outright that it destroyed the session, act on it now
  // rather than leaving a logged-in shell that fails one request at a time.
  useEffect(() => {
    if (!isLoggedIn) return;
    return onSessionTerminated(() => {
      const currentPath = location.pathname + location.search + location.hash;
      setUser(null);
      setLoggedIn(false);
      navigate(`/login?redirectUrl=${encodeURIComponent(currentPath)}`, {
        replace: true,
      });
    });
  }, [
    isLoggedIn,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    setLoggedIn,
    setUser,
  ]);

  useEffect(() => {
    if (!isLoggedIn) return;
    return installSessionActivityHeartbeat({
      target: document,
      send: () => {
        if (typeof fetch !== 'function') return;
        void sessionFetch('/api/authentication/activity', {
          method: 'POST',
          credentials: 'include',
          keepalive: true,
        });
      },
    });
  }, [isLoggedIn]);

  return (
    <SessionCheckContext.Provider value={{ isCheckingSession, lastCheckTime }}>
      {children}
    </SessionCheckContext.Provider>
  );
};

export const useSessionCheck = () => useContext(SessionCheckContext);
